import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import { format } from 'date-fns';
import {
  useGetNotificationGroupsQuery,
  useDeleteNotificationGroupMutation
} from '../../services/connectApi';
import type { NotificationGroup } from '../../models';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingBackdrop from '../../components/LoadingBackdrop';
import { useSnackbar } from 'notistack';

const GroupsList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedGroup, setSelectedGroup] = useState<NotificationGroup | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { data, isLoading, refetch } = useGetNotificationGroupsQuery({ page: page + 1, page_size: pageSize });
  const groups = data?.results || [];
  React.useEffect(() => {
    if (data?.count !== undefined) setTotalCount(data.count);
  }, [data]);

  const [deleteGroup, { isLoading: isDeleting }] = useDeleteNotificationGroupMutation();

  const handleEdit = (id: string) => {
    navigate(`/connect/groups/${id}`);
  };

  const handleDeleteClick = (group: NotificationGroup) => {
    setSelectedGroup(group);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedGroup) {
      try {
        await deleteGroup(selectedGroup.id).unwrap();
        enqueueSnackbar('Group deleted successfully', { variant: 'success' });
        refetch();
      } catch (error) {
        enqueueSnackbar('Failed to delete group', { variant: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250
    },
    {
      field: 'users_count',
      headerName: 'Users',
      width: 120,
      renderCell: (params: GridRenderCellParams<NotificationGroup, any>) => (
        <Chip
          icon={<PeopleIcon />}
          label={params.value}
          color="primary"
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'has_filter',
      headerName: 'Dynamic Filter',
      width: 150,
      valueGetter: (_value, row) => {
        const group = row as NotificationGroup;
        return Object.keys(group.query_filter || {}).length > 0;
      },
      renderCell: (params) => (
        params.value ? (
          <Chip label="Dynamic" color="info" size="small" />
        ) : (
          <Chip label="Static" color="default" size="small" />
        )
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      minWidth: 180,
      valueFormatter: (value) => {
        return value ? format(new Date(value), 'MMM d, yyyy HH:mm') : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<NotificationGroup, any>) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(params.row.id)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(params.row)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return <Typography>Loading groups...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Notification Groups
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Create and manage user groups for targeted notifications
      </Typography>

      <Paper elevation={2} sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={groups}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            setPage(newPage);
            setPageSize(newPageSize);
          }}
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: 'updated_at', sort: 'desc' }],
            },
          }}
        />
      </Paper>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Group"
        content={`Are you sure you want to delete the group "${selectedGroup?.name}"? This action cannot be undone.`}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <LoadingBackdrop open={isDeleting} />
    </Box>
  );
};

export default GroupsList;
